import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { HostApiService } from './host-api.service';
import { Host } from './models/host.model';
import { HostContextService } from './host-context.service';
import { ROUTE_PARAMS, ROUTE_PATHS } from './config/route-config';

export interface HostContextData {
  host: Host;
}

/**
 * Resolver that loads host context based on route parameter or stored host ID.
 *
 * Flow:
 * 1. Check if hostId is in route params
 * 2. If not, check localStorage for previously selected host
 * 3. Fetch host data from backend
 * 4. If host exists, set in context and allow navigation
 * 5. If no host found, redirect to onboarding
 */
export const hostContextResolver: ResolveFn<HostContextData | null> = (
  route: ActivatedRouteSnapshot
) => {
  const hostApiService = inject(HostApiService);
  const hostContextService = inject(HostContextService);
  const router = inject(Router);

  // Get hostId from route params or localStorage
  const hostIdFromRoute = route.paramMap.get(ROUTE_PARAMS.HOST_ID);
  const storedHostId = hostContextService.getStoredHostId();
  const hostId = hostIdFromRoute || storedHostId;

  if (!hostId) {
    // No host ID available, redirect to onboarding
    router.navigate(['/', ROUTE_PATHS.ONBOARDING]);
    return of(null);
  }

  // Fetch all hosts from backend and find the specific one
  return hostApiService.getUserHosts().pipe(
    map((hosts) => {
      // Find the specific host by ID
      const host = hosts.find((h) => h.id === hostId);

      if (!host) {
        // Host not found in user's hosts
        throw new Error(`Host with ID ${hostId} not found`);
      }

      // Host found, set in context
      hostContextService.setCurrentHost(host);
      hostContextService.setAvailableHosts(hosts);

      return { host };
    }),
    catchError(() => {
      // Host not found or error occurred
      // Clear invalid stored host ID
      hostContextService.clearContext();

      // Redirect to onboarding
      router.navigate(['/', ROUTE_PATHS.ONBOARDING]);
      return of(null);
    })
  );
};
